import React, { useState, ReactElement } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.scss';
import { LoansErrorHookProps } from '../types';
import { copyErrorToClipboard, ErrorItem } from './clipboardUtils';

type Props = {
  mappedErrors?: Record<string, LoansErrorHookProps>;
};

export const ErrorListWithToast: React.FC<Props> = ({ mappedErrors }): ReactElement => {
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastKey, setToastKey] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<string>(' ');
  const [toastVariant, setToastVariant] = useState<ToastNotificationVariant>(
    ToastNotificationVariant.INFO
  );

  const { t, i18n } = useTranslation('HbtPortfolioManagementRequestOverview');

  const currentLang = i18n.language || 'en';

  const handleCopy = (error: LoansErrorHookProps): void => {
    const messageToCopy = currentLang.startsWith('fr')
      ? error.errorMessageFrench
      : error.errorMessageEnglish;

    const item: ErrorItem = {
      message: [messageToCopy],
      subheading: error.errorMessageDescriptionID
    };

    copyErrorToClipboard(
      item,
      () => {
        setToastMessage(t('PI-Portfolio-Management-Loans-Copied'));
        setToastVariant(ToastNotificationVariant.INFO);
        setToastKey((prev) => prev + 1);
        setToastOpen(true);
      },
      (err) => {
        setToastMessage(`${t('Error')} - ${err}`);
        setToastVariant(ToastNotificationVariant.INFO);
        setToastKey((prev) => prev + 1);
        setToastOpen(true);
      }
    );
  };

  return (
    <>
      {Object.entries(mappedErrors ?? {}).map(([code, error], idx) => {
        const messageToShow = currentLang.startsWith('fr')
          ? error.errorMessageFrench
          : error.errorMessageEnglish;

        return (
          <Box key={code} className={styles.errorItem}>
            <Box
              className={styles.errorHeader}
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle2" className={styles.errorText}>
                  {error.errorMessageDescriptionID}
                </Typography>
                <Typography variant="body2" className={`${styles.errorBody} ${styles.errorText}`}>
                  {messageToShow}
                </Typography>
              </Box>

              <Button
                id={`copy-button-${idx}`}
                size={ButtonSize.LARGE}
                variant={ButtonVariant.TERTIARY}
                leadingIcon={() => <CmhcIcon size={CmhcIconSize.LARGE} type="icon_container" />}
                onClick={() => handleCopy(error)}
                className={styles.copyButton}
                name="copyButton"
                text={{ value: ' ' }}
              />
            </Box>
          </Box>
        );
      })}
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          key={toastKey}
          open={toastOpen}
          header={{ value: toastMessage }}
          variant={toastVariant}
          icon={
            toastVariant === ToastNotificationVariant.SUCCESS
              ? 'icon_error_outlined'
              : 'icon_check_circle'
          }
          onClose={() => setToastOpen(false)}
          subHeader={{ value: String(' ') }}
        />
      </Box>
    </>
  );
};

export default { ErrorListWithToast, copyErrorToClipboard };
export type { ErrorItem };
